<script setup lang="ts">
import { mdiCheck, mdiFormTextboxPassword, mdiLock } from "@mdi/js";
import { useFormContext } from "@soft-db/form";
import { useApi } from "@soft-db/monitoring-api-client";
import { Button, FormField, Icon, InputGroup, Message, TextInput, Tooltip } from "@soft-db/ui";
import { z } from "zod";
import FieldPermissionGuard from "../permission/FieldPermissionGuard.vue";

const conflictError = "conflict" as const;

const { t } = useI18n();

const api = useApi().station();

const props = defineProps<{
	field: string;
	initialSn?: string;
	disabled?: boolean;
	lock?: boolean;
	hideIcon?: boolean;
}>();

const { useField } = useFormContext<Record<string, string>>();

const locked = ref(props.lock);
function removeLock() {
	locked.value = false;
}

const runningValidation = ref(false);
const lastCheckValid = ref(false);

const serialNumber = useField(props.field, {
	asyncDebounceMs: 500,
	validators: {
		onChange: z.string().min(1),
		onChangeAsync: z.string().refine(async () => {
			if (!serialNumberChanged.value || lastCheckValid.value) {
				return true;
			}

			if (!serialNumber.data.value) {
				return true;
			}

			runningValidation.value = true;
			try {
				const available = await api.serialNumberIsFree(serialNumber.data.value);
				if (available) {
					lastCheckValid.value = true;
				}
				return available;
			} finally {
				setTimeout(() => {
					runningValidation.value = false;
					pendingValidation.value = false;
				}, 500);
			}
		}, conflictError),
	},
});

const pendingValidation = ref(false);
watch(serialNumber.data, () => {
	lastCheckValid.value = false;
	pendingValidation.value = true;
});

const serialNumberChanged = computed(() => {
	return serialNumber.data.value !== props.initialSn;
});

const serialNumberAvailabilityState = computed(() => {
	if (!serialNumber.data.value) {
		return null;
	}

	if (!serialNumberChanged.value) {
		return null;
	}

	if (runningValidation.value) {
		return "running";
	}

	if (pendingValidation.value) {
		return null;
	}

	if (serialNumber.errorMap.value.onChange === conflictError) {
		return "conflict";
	} else {
		return "ok";
	}
});

const error = computed(() => {
	return serialNumber.errorMap.value.onChange;
});
</script>

<template>
	<FormField class="w-full" :label="t('station.serialNumber')">
		<InputGroup class="w-full">
			<Icon v-if="!hideIcon" :d="mdiFormTextboxPassword" />

			<FieldPermissionGuard resource="station" field="serialNumber">
				<TextInput
					v-model="serialNumber.data.value"
					:disabled="disabled || locked"
					@blur="serialNumber.handleBlur"
				/>
				<Tooltip :tooltip="t('station-settings.updateSN')">
					<Button v-if="locked" variant="ghost" size="xs" color="secondary" @click="removeLock">
						<Icon :d="mdiLock" />
					</Button>
				</Tooltip>

				<template #fallback>
					<TextInput class="muted" :modelValue="serialNumber.data.value" readonly />
				</template>
			</FieldPermissionGuard>
		</InputGroup>

		<Message v-if="error != null && error != conflictError" class="label" severity="error">
			{{ serialNumber.errorMap.value.onChange }}
		</Message>

		<Message
			v-if="serialNumberAvailabilityState === 'conflict'"
			class="font-semibold"
			severity="error"
		>
			{{ t("station.serialNumberTaken") }}
		</Message>

		<Message
			v-else-if="serialNumberAvailabilityState === 'ok'"
			class="text-success flex items-center gap-1 font-semibold"
		>
			<Icon :d="mdiCheck" />
			{{ t("station.serialNumberAvailable") }}
		</Message>

		<Message
			v-else-if="
				serialNumberAvailabilityState === 'running' ||
				(serialNumberChanged && serialNumber.errorMap.value.onChange == null)
			"
		>
			<span class="loading loading-dots text-primary loading-xs" />
			{{ t("station.serialNumberValidating") }}
		</Message>
	</FormField>
</template>
